/*
 * YourXI achievement icons, drawn in kit style A:
 * flat fills, thin black hairline on every piece, soft grey oval shadow underneath.
 *
 * iconSVG(name, opts) -> SVG string ('' for 'none' unless opts.placeholder)
 *
 * name  = 'cup' | 'leagueTrophy' | 'shield' | 'medal' | 'football' | 'boot' | 'star' | 'promotion' | 'none'
 * opts  = {
 *   finish:      'mono' | 'gold' | 'silver' | 'bronze'   (default 'mono')
 *   size:        px. Sets width/height and keeps the hairline crisp (never thinner than ~0.8px).
 *                Below 40px the small highlight facets are dropped so the icon stays clean.
 *   onDark:      true on a dark background: lighter shadow + pale rim behind the hairline
 *   ink:         hairline colour (default '#111111')
 *   ribbon:      ['#hex', '#hex'] medal ribbon straps
 *   shadow:      false to leave the shadow off
 *   hairline:    stroke width in the 100-unit box, overrides the size rule
 *   placeholder: true makes 'none' draw a light "no icon" glyph (for pickers)
 * }
 *
 * All icons share one 100 x 100 box, stand on the same ground line (y 88) and
 * fill a similar area, so they read at the same weight side by side.
 */
(function (root) {
  'use strict';

  var FINISHES = {
    mono:   { label: 'Mono',   base: '#F4F3EF', light: '#FFFFFF', dark: '#CFCEC8', ribbon: ['#FFFFFF', '#D9D8D3'], patch: '#111111' },
    gold:   { label: 'Gold',   base: '#D9B44A', light: '#F0D47E', dark: '#A88427', ribbon: ['#14213D', '#C8102E'] },
    silver: { label: 'Silver', base: '#C5CAD1', light: '#E9ECF0', dark: '#959BA4', ribbon: ['#14213D', '#C8102E'] },
    bronze: { label: 'Bronze', base: '#C4834A', light: '#E0A36C', dark: '#93602F', ribbon: ['#14213D', '#C8102E'] }
  };
  var ICONS = [
    { id: 'none', label: 'None' },
    { id: 'cup', label: 'Cup' },
    { id: 'leagueTrophy', label: 'League trophy' },
    { id: 'shield', label: 'Shield' },
    { id: 'medal', label: 'Medal' },
    { id: 'football', label: 'Football' },
    { id: 'boot', label: 'Boot' },
    { id: 'star', label: 'Star' },
    { id: 'promotion', label: 'Promotion' }
  ];
  // same values as kits.js NIGHT, so kits and icons match on a dark page
  var NIGHT = { shadow: '#9AA3B2', shadowOpacity: '.30', halo: '#FFFFFF', haloOpacity: '.42' };
  var uid = 0;

  function mirror(d) { // mirror a path across x = 50 (absolute commands only)
    return d.replace(/(-?\d*\.?\d+) (-?\d*\.?\d+)/g, function (m, x, y) { return +(100 - x).toFixed(2) + ' ' + y; })
      .replace(/H(-?\d*\.?\d+)/g, function (m, x) { return 'H' + +(100 - x).toFixed(2); });
  }
  function circ(cx, cy, r) {
    return 'M' + (cx - r) + ' ' + cy + ' a' + r + ' ' + r + ' 0 1 0 ' + (2 * r) + ' 0 a' + r + ' ' + r + ' 0 1 0 ' + (-2 * r) + ' 0 Z';
  }
  function starPts(cx, cy, R, r) {
    var o = [], i = [];
    for (var k = 0; k < 5; k++) {
      var a = (-90 + 72 * k) * Math.PI / 180, b = (-54 + 72 * k) * Math.PI / 180;
      o.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
      i.push([cx + r * Math.cos(b), cy + r * Math.sin(b)]);
    }
    return { o: o, i: i };
  }
  function poly(pts) { return 'M' + pts.map(function (p) { return p[0].toFixed(2) + ' ' + p[1].toFixed(2); }).join(' L') + ' Z'; }
  function starD(cx, cy, R, r) {
    var s = starPts(cx, cy, R, r), pts = [];
    for (var k = 0; k < 5; k++) { pts.push(s.o[k]); pts.push(s.i[k]); }
    return poly(pts);
  }

  /* Each builder returns a list of parts:
   *   { d, fill }                 filled piece with hairline
   *   { d, fill, detail:true }    highlight facet, dropped below 40px
   *   { tube:d, w, fill }         thick stroke (handles), hairline on both edges
   *   { line:d, w }               ink line (laces, seams), width in hairlines
   *   { clip:d, parts:[...] }     parts clipped to a shape (ball patches)
   * `ground` = y of the shadow, `rx` = shadow width
   */
  var BUILD = {
    cup: function (m) {
      var hL = 'M28 23 C9 20 9 47 35 47';
      return { rx: 27, parts: [
        { tube: hL, w: 4.6, fill: m.base }, { tube: mirror(hL), w: 4.6, fill: m.base },
        { d: 'M26 17 H74 C74 38 66 52 50 55 C34 52 26 38 26 17 Z', fill: m.base },
        { d: 'M31 20 H38 C38 33 42 43 47 50 C38 47 31 38 31 20 Z', fill: m.light, detail: true },
        { d: 'M23 11 H77 L75 17 H25 Z', fill: m.base },
        { d: 'M46 55 H54 L53 63 H47 Z', fill: m.dark },
        { d: 'M41 63 H59 L61 67 H39 Z', fill: m.base },
        { d: 'M39 67 H61 C61 71 65 74 70 76 H30 C35 74 39 71 39 67 Z', fill: m.base },
        { d: 'M26 76 H74 V88 H26 Z', fill: m.dark },
        { d: 'M38 79.5 H62 V84.5 H38 Z', fill: m.light, detail: true }
      ] };
    },
    leagueTrophy: function (m) {
      var hL = 'M42 25 C29 23 29 42 45 46';
      return { rx: 22, parts: [
        { tube: hL, w: 3.6, fill: m.base }, { tube: mirror(hL), w: 3.6, fill: m.base },
        { d: circ(50, 8.5, 3.6), fill: m.base },
        { d: 'M41 12 H59 L57 18 H43 Z', fill: m.dark },
        { d: 'M41 18 H59 C62 30 61 42 56 50 C54 53 53 56 53 60 H47 C47 56 46 53 44 50 C39 42 38 30 41 18 Z', fill: m.base },
        { d: 'M44 21 H48 C47 31 48 41 50 48 C45 43 42 33 44 21 Z', fill: m.light, detail: true },
        { d: 'M46.5 60 H53.5 V65 H46.5 Z', fill: m.dark },
        { d: 'M40 65 H60 L62 70 H38 Z', fill: m.base },
        { d: 'M34 70 H66 V88 H34 Z', fill: m.dark },
        { d: 'M40 75 H60 V82 H40 Z', fill: m.light, detail: true }
      ] };
    },
    shield: function (m) {
      return { rx: 25, parts: [
        { d: 'M20 11 H80 V42 C80 63 67 78 50 88 C33 78 20 63 20 42 Z', fill: m.base },
        { d: 'M27 18 H73 V42 C73 58 63 71 50 79 C37 71 27 58 27 42 Z', fill: m.light, detail: true },
        { d: 'M27 36 L50 50 L73 36 V45 L50 59 L27 45 Z', fill: m.dark }
      ] };
    },
    medal: function (m, o) {
      var rb = o.ribbon || m.ribbon;
      return { rx: 22, parts: [
        { d: 'M28 5 H45 L58 45 H46 Z', fill: rb[0] },
        { d: 'M72 5 H55 L42 45 H54 Z', fill: rb[1] },
        { d: 'M43.5 41 H56.5 V47 H43.5 Z', fill: m.dark },
        { d: circ(50, 66, 22), fill: m.base },
        { d: circ(50, 66, 15.5), fill: m.light, detail: true },
        { d: starD(50, 67, 10, 4.2), fill: m.dark }
      ] };
    },
    football: function (m, o, finish) {
      var cx = 50, cy = 51, R = 37, mono = finish === 'mono';
      var ball = mono ? '#FFFFFF' : m.base, patch = mono ? m.patch : m.dark;
      var inner = [], seams = [], k, a;
      var P = function (r, deg) { a = deg * Math.PI / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; };
      var c5 = []; for (k = 0; k < 5; k++) c5.push(P(11, -90 + 72 * k));
      inner.push({ d: poly(c5), fill: patch });
      for (k = 0; k < 5; k++) {
        var ang = -90 + 72 * k, tip = P(22, ang);
        seams.push('M' + c5[k][0].toFixed(2) + ' ' + c5[k][1].toFixed(2) + ' L' + tip[0].toFixed(2) + ' ' + tip[1].toFixed(2));
        // outer pentagon, one point facing the centre, partly off the edge of the ball
        var c = P(33, ang), pts = [];
        for (var j = 0; j < 5; j++) { var b = (ang + 180 + 72 * j) * Math.PI / 180; pts.push([c[0] + 11 * Math.cos(b), c[1] + 11 * Math.sin(b)]); }
        inner.push({ d: poly(pts), fill: patch });
        // seams from the side corners of the outer patch towards the neighbours
        var s1 = pts[1], s4 = pts[4];
        var e1 = P(R + 2, ang + 36), e4 = P(R + 2, ang - 36);
        seams.push('M' + s1[0].toFixed(2) + ' ' + s1[1].toFixed(2) + ' L' + ((s1[0] + e1[0]) / 2).toFixed(2) + ' ' + ((s1[1] + e1[1]) / 2).toFixed(2));
        seams.push('M' + s4[0].toFixed(2) + ' ' + s4[1].toFixed(2) + ' L' + ((s4[0] + e4[0]) / 2).toFixed(2) + ' ' + ((s4[1] + e4[1]) / 2).toFixed(2));
      }
      return { rx: 25, parts: [
        { d: circ(cx, cy, R), fill: ball },
        { clip: circ(cx, cy, R), parts: inner.concat([{ line: seams.join(' '), w: 1 }]) },
        { d: circ(cx, cy, R), fill: 'none' }
      ] };
    },
    boot: function (m, o, finish) {
      var up = finish === 'mono' ? '#FFFFFF' : m.base;
      return { rx: 30, ty: 18, parts: [
        { d: 'M16 36 C16 27 20 20 24 18 H41 C43 26 49 33 60 37 L79 42 C87 44 90 49 89 55 L88 58 H16 Z', fill: up },
        { d: 'M16 40 C16 33 18 28 21 25 L29 25 C28 34 30 46 34 58 H16 Z', fill: m.dark },
        { d: 'M24 18 H41 C41.4 20.5 42 22.5 43 24.5 H21.5 C22 21.5 23 19.5 24 18 Z', fill: m.light, detail: true },
        { line: 'M44.5 27.5 L49 24.5 M48.5 31.5 L53 28.5 M53 34.5 L57 31.5 M58 36.5 L61.5 33.5', w: 1.6, detail: true },
        { d: 'M14 58 H90 C90 62 87 64 82 64 H19 C16 64 14 62 14 58 Z', fill: m.dark },
        { d: 'M21 64 H28 L27.5 70 H21.5 Z', fill: m.dark }, { d: 'M33 64 H39 L38.5 70 H33.5 Z', fill: m.dark },
        { d: 'M58 64 H64 L63.5 70 H58.5 Z', fill: m.dark }, { d: 'M71 64 H78 L77.5 70 H71.5 Z', fill: m.dark }
      ] };
    },
    star: function (m, o, finish, small) {
      var cx = 50, cy = 52, R = 41, r = 17, s = starPts(cx, cy, R, r), parts = [{ d: starD(cx, cy, R, r), fill: m.base }];
      if (!small) for (var k = 0; k < 5; k++) // bevel: the left facet of every point is lighter
        parts.push({ d: poly([[cx, cy], s.i[(k + 4) % 5], s.o[k]]), fill: m.light, detail: true, thin: true });
      return { rx: 24, parts: parts };
    },
    promotion: function (m) {
      return { rx: 22, parts: [
        { d: 'M50 9 L84 46 H65 V87 H35 V46 H16 Z', fill: m.base },
        { d: 'M50 9 L16 46 H35 V87 H50 Z', fill: m.light, detail: true }
      ] };
    }
  };

  function iconSVG(name, opts) {
    var o = opts || {};
    var finish = FINISHES[o.finish] ? o.finish : 'mono', m = FINISHES[finish];
    var size = o.size || null, small = size && size < 40;
    var sw = o.hairline || (size ? Math.max(0.5, Math.min(3.6, 80 / size)) : 0.9);
    var ink = o.ink || '#111111', dark = !!o.onDark;
    var dims = size ? ' width="' + size + '" height="' + size + '"' : '';
    var open = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"' + dims + ' class="xi-icon" aria-hidden="true">';

    if (!name || name === 'none' || !BUILD[name]) {
      if (!o.placeholder) return '';
      var g = dark ? '#5B6474' : '#C9C8C2';
      return open + '<path d="' + circ(50, 50, 30) + ' M29 71 L71 29" fill="none" stroke="' + g + '" stroke-width="' + Math.max(sw * 1.6, 2) + '" stroke-linecap="round"/></svg>';
    }

    var spec = BUILD[name](m, o, finish, small), id = 'xi' + (++uid) + Math.random().toString(36).slice(2, 6);
    var defs = '', out = [], halo = [];
    var strokeA = ' stroke="' + ink + '" stroke-width="' + sw.toFixed(2) + '" stroke-linejoin="round" stroke-linecap="round"';

    function draw(list, into, haloInto) {
      list.forEach(function (p) {
        if (p.detail && small) return;
        if (p.clip) {
          var cid = id + 'c' + into.length;
          defs += '<clipPath id="' + cid + '"><path d="' + p.clip + '"/></clipPath>';
          var sub = []; draw(p.parts, sub, null);
          into.push('<g clip-path="url(#' + cid + ')">' + sub.join('') + '</g>');
        } else if (p.tube) {
          into.push('<path d="' + p.tube + '" fill="none" stroke="' + ink + '" stroke-width="' + (p.w + 2 * sw).toFixed(2) + '" stroke-linecap="round"/>' +
            '<path d="' + p.tube + '" fill="none" stroke="' + p.fill + '" stroke-width="' + p.w + '" stroke-linecap="round"/>');
          if (haloInto) haloInto.push('<path d="' + p.tube + '" fill="none" stroke-width="' + (p.w + 2 * sw + sw * 4.5).toFixed(2) + '" stroke-linecap="round"/>');
        } else if (p.line) {
          into.push('<path d="' + p.line + '" fill="none" stroke="' + ink + '" stroke-width="' + (sw * p.w).toFixed(2) + '" stroke-linecap="round"/>');
        } else {
          into.push('<path d="' + p.d + '" fill="' + p.fill + '"' + strokeA.replace('stroke-width="' + sw.toFixed(2) + '"', 'stroke-width="' + (p.thin ? sw * 0.6 : sw).toFixed(2) + '"') + '/>');
          if (haloInto && !p.detail) haloInto.push('<path d="' + p.d + '" fill="none" stroke-width="' + (sw * 4.5).toFixed(2) + '" stroke-linejoin="round"/>');
        }
      });
    }
    draw(spec.parts, out, halo);

    var ty = spec.ty || 0, ground = 93;
    var shadow = '';
    if (o.shadow !== false) {
      defs += '<filter id="' + id + 'f" x="-50%" y="-300%" width="200%" height="700%"><feGaussianBlur stdDeviation="' + (small ? 1.6 : 2.2) + '"/></filter>';
      shadow = '<ellipse cx="50" cy="' + ground + '" rx="' + spec.rx + '" ry="3.2" fill="' + (dark ? NIGHT.shadow : '#000') + '" opacity="' + (dark ? NIGHT.shadowOpacity : '.30') + '" filter="url(#' + id + 'f)"/>';
    }
    var haloG = dark ? '<g stroke="' + NIGHT.halo + '" stroke-opacity="' + NIGHT.haloOpacity + '">' + halo.join('') + '</g>' : '';
    var body = haloG + out.join('');
    if (ty) body = '<g transform="translate(0 ' + ty + ')">' + body + '</g>';
    return open + (defs ? '<defs>' + defs + '</defs>' : '') + shadow + body + '</svg>';
  }

  var api = { iconSVG: iconSVG, ICONS: ICONS, FINISHES: FINISHES, ICON_FINISHES: Object.keys(FINISHES) };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else { root.YourXIIcons = api; }
})(this);
